import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    Building2,
    Check,
    Dumbbell,
    Loader2,
    RefreshCw,
    Search,
    Tags,
} from 'lucide-react';

function gymNames(gyms) {
    return (gyms || []).map((g) => g.gym_name).filter(Boolean);
}

export default function ExerciseGymAliasesPanel() {
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();

    const [exercises, setExercises] = useState([]);
    const [stats, setStats] = useState({ exercises: 0, aliases: 0, gyms: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState('');
    const [pending, setPending] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchReview = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get('/gym_exercise_aliases.php', {
                params: { action: 'admin_review' },
            });
            if (res.data?.success) {
                setExercises(res.data.exercises || []);
                setStats(res.data.stats || { exercises: 0, aliases: 0, gyms: 0 });
            } else {
                setError(res.data?.error || 'Impossibile caricare gli alias');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Errore di connessione al server');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchReview();
    }, [fetchReview]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return exercises;
        return exercises.filter((ex) => {
            const hay = [
                ex.nome_catalogo,
                ex.gruppo_muscolare,
                String(ex.esercizio_id),
                ...(ex.variants || []).flatMap((v) => [v.custom_name, ...gymNames(v.gyms)]),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [exercises, search]);

    const promote = async () => {
        if (!pending || saving) return;
        setSaving(true);
        setError('');
        try {
            const res = await api.post('/gym_exercise_aliases.php', {
                action: 'promote_catalog',
                esercizio_id: pending.esercizio_id,
                new_name: pending.custom_name,
            });
            if (!res.data?.success) {
                throw new Error(res.data?.error || 'Promozione non riuscita');
            }
            setMessage({
                type: 'success',
                text: `Catalogo aggiornato: “${res.data.nome_precedente}” → “${res.data.nome_catalogo}”. Alias allineati rimossi: ${res.data.aliases_rimossi}.`,
            });
            setPending(null);
            await fetchReview();
            setTimeout(() => setMessage(null), 5000);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Promozione non riuscita');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Nomi modificati dalle palestre
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                        Raggruppati per esercizio del catalogo. Se un nome palestra è migliore, lo promuovi a catalogo globale: le altre palestre lo vedono, chi aveva già quel nome perde l’alias, chi ha un nome diverso lo tiene.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={fetchReview}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                    <RefreshCw className="h-4 w-4" />
                    Aggiorna
                </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Esercizi</p>
                    <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
                        <Dumbbell className="h-4 w-4 text-indigo-500" />
                        {stats.exercises}
                    </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Alias</p>
                    <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
                        <Tags className="h-4 w-4 text-amber-500" />
                        {stats.aliases}
                    </p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Palestre</p>
                    <p className="mt-1 flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
                        <Building2 className="h-4 w-4 text-emerald-500" />
                        {stats.gyms}
                    </p>
                </div>
            </div>

            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Cerca catalogo, nome palestra o palestra…"
                    className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
            </div>

            {message ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
                    {message.text}
                </div>
            ) : null}
            {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-gray-500 dark:text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Caricamento…
                </div>
            ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    {exercises.length === 0
                        ? 'Nessuna palestra ha ancora rinominato un esercizio.'
                        : 'Nessun risultato per questa ricerca.'}
                </div>
            ) : (
                <div className="space-y-4">
                    {filtered.map((ex) => (
                        <article
                            key={ex.esercizio_id}
                            className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                        >
                            <header className="border-b border-gray-100 bg-gray-50/80 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Catalogo
                                        </p>
                                        <h4 className="truncate text-base font-semibold text-gray-900 dark:text-white">
                                            {ex.nome_catalogo}
                                        </h4>
                                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                            #{ex.esercizio_id}
                                            {ex.gruppo_muscolare ? ` · ${ex.gruppo_muscolare}` : ''}
                                            {` · ${ex.gym_count} ${ex.gym_count === 1 ? 'palestra' : 'palestre'} · ${ex.variant_count} ${ex.variant_count === 1 ? 'nome' : 'nomi'}`}
                                        </p>
                                    </div>
                                </div>
                            </header>
                            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                {(ex.variants || []).map((variant) => (
                                    <li key={`${ex.esercizio_id}-${variant.custom_name}`} className="px-4 py-3">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                                                    {variant.custom_name}
                                                </p>
                                                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                                                    {variant.gym_count} {variant.gym_count === 1 ? 'palestra' : 'palestre'}:{' '}
                                                    {gymNames(variant.gyms).join(', ')}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setPending({
                                                    esercizio_id: ex.esercizio_id,
                                                    nome_catalogo: ex.nome_catalogo,
                                                    custom_name: variant.custom_name,
                                                    gyms: variant.gyms,
                                                    otherGyms: (ex.variants || [])
                                                        .filter((v) => v.custom_name !== variant.custom_name)
                                                        .flatMap((v) => v.gyms.map((g) => ({ ...g, keep_name: v.custom_name }))),
                                                })}
                                                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                                Usa per tutti
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    ))}
                </div>
            )}

            {pending ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Promuovere questo nome a catalogo?
                        </h4>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            Il catalogo globale passa da <span className="font-semibold">“{pending.nome_catalogo}”</span> a <span className="font-semibold">“{pending.custom_name}”</span>.
                        </p>
                        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-300">
                            <li>
                                Chi non ha un alias vedrà il nuovo nome.
                            </li>
                            <li>
                                {gymNames(pending.gyms).join(', ') || 'Le palestre con questo nome'} perderanno l’alias (diventa catalogo).
                            </li>
                            {pending.otherGyms?.length > 0 ? (
                                <li>
                                    Le altre palestre tengono il loro nome:{' '}
                                    {pending.otherGyms.map((g) => `${g.gym_name} (“${g.keep_name}”)`).join(', ')}.
                                </li>
                            ) : (
                                <li>Nessun’altra palestra ha un nome diverso su questo esercizio.</li>
                            )}
                        </ul>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => setPending(null)}
                                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                disabled={saving}
                                onClick={promote}
                                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                Conferma per tutti
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
